'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Participant {
  id: string;
  name: string;
  initials: string;
  role: 'consultant' | 'client';
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  isSpeaking: boolean;
}

interface ChatMessage {
  id: string;
  sender: string;
  text: string;
  timestamp: string;
}

// ─── Mock Session Data ─────────────────────────────────────────────────────────
const sessionData = {
  sessionId: 'sess-001',
  title: 'Islamic Finance Consultation',
  consultant: {
    id: 'c1',
    name: 'Yusuf Al-Rashid',
    initials: 'YR',
    role: 'consultant' as const,
    title: 'Islamic Finance & Sukuk Expert',
  },
  client: {
    id: 'cl1',
    name: 'Ahmed Hassan',
    initials: 'AH',
    role: 'client' as const,
  },
  scheduledDuration: 60,
  startTime: new Date(),
};

const mockMessages: ChatMessage[] = [
  { id: 'm1', sender: 'Yusuf Al-Rashid', text: 'Assalamu Alaikum! Ready to begin?', timestamp: '10:00 AM' },
  { id: 'm2', sender: 'Ahmed Hassan', text: 'Wa Alaikum Assalam! Yes, let\'s start.', timestamp: '10:01 AM' },
];

// ─── Component ─────────────────────────────────────────────────────────────────
export default function VideoCallPage() {
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>(mockMessages);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'poor'>('connecting');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pinnedParticipant, setPinnedParticipant] = useState<string | null>('c1');
  const chatEndRef = useRef<HTMLDivElement>(null);

  const participants: Participant[] = [
    {
      id: 'c1',
      name: sessionData.consultant.name,
      initials: sessionData.consultant.initials,
      role: 'consultant',
      isMuted: false,
      isCameraOff: false,
      isScreenSharing,
      isSpeaking: true,
    },
    {
      id: 'cl1',
      name: sessionData.client.name,
      initials: sessionData.client.initials,
      role: 'client',
      isMuted,
      isCameraOff,
      isScreenSharing: false,
      isSpeaking: false,
    },
  ];

  // Simulate connection
  useEffect(() => {
    const t = setTimeout(() => setConnectionStatus('connected'), 1800);
    return () => clearTimeout(t);
  }, []);

  // Session timer
  useEffect(() => {
    if (sessionEnded || connectionStatus !== 'connected') return;
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [sessionEnded, connectionStatus]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSendMessage = useCallback(() => {
    if (!chatInput.trim()) return;
    const newMsg: ChatMessage = {
      id: `m${Date.now()}`,
      sender: sessionData.client.name,
      text: chatInput.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((prev) => [...prev, newMsg]);
    setChatInput('');
  }, [chatInput]);

  const handleEndSession = () => {
    setSessionEnded(true);
    setShowEndModal(false);
  };

  const toggleScreenShare = () => {
    setIsScreenSharing((prev) => !prev);
  };

  // ─── Session Ended Screen ───────────────────────────────────────────────────
  if (sessionEnded) {
    return (
      <div className="min-h-screen bg-[#0d0f14] flex items-center justify-center p-6">
        <div className="bg-[#161a24] border border-white/10 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-5">
            <Icon name="CheckCircleIcon" size={36} className="text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Session Ended</h2>
          <p className="text-white/50 text-sm mb-1">{sessionData.title}</p>
          <p className="text-white/30 text-xs mb-6">Duration: {formatTime(elapsedSeconds)}</p>

          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Consultant</span>
              <span className="text-white font-medium">{sessionData.consultant.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Session</span>
              <span className="text-white font-medium">{sessionData.scheduledDuration} min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Status</span>
              <span className="text-emerald-400 font-medium">Completed</span>
            </div>
          </div>

          <div className="flex gap-3">
            <Link
              href="/my-bookings"
              className="flex-1 py-2.5 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/15 transition-colors text-center"
            >
              My Bookings
            </Link>
            <Link
              href="/freelance-consultants"
              className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors text-center"
            >
              Book Again
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Connecting Screen ──────────────────────────────────────────────────────
  if (connectionStatus === 'connecting') {
    return (
      <div className="min-h-screen bg-[#0d0f14] flex items-center justify-center p-6">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
            <div className="absolute inset-2 rounded-full border-2 border-primary/50 animate-ping animation-delay-300" />
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
              <Icon name="VideoCameraIcon" size={32} className="text-primary" />
            </div>
          </div>
          <h2 className="text-white text-xl font-semibold mb-2">Joining Session…</h2>
          <p className="text-white/40 text-sm">{sessionData.title}</p>
        </div>
      </div>
    );
  }

  const pinnedParticipant_ = participants.find((p) => p.id === pinnedParticipant) ?? participants[0];
  const thumbnailParticipants = participants.filter((p) => p.id !== pinnedParticipant_?.id);

  return (
    <div className={`flex flex-col bg-[#0d0f14] ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}`}>
      {/* ── Top Bar ── */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#0d0f14]/90 backdrop-blur border-b border-white/5 z-10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`} />
            <span className="text-white/50 text-xs">{connectionStatus === 'connected' ? 'Connected' : 'Poor connection'}</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div>
            <p className="text-white text-sm font-semibold leading-tight">{sessionData.title}</p>
            <p className="text-white/40 text-xs">{sessionData.consultant.title}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Timer */}
          <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-3 py-1.5">
            <Icon name="ClockIcon" size={14} className="text-white/40" />
            <span className="text-white font-mono text-sm">{formatTime(elapsedSeconds)}</span>
            <span className="text-white/30 text-xs">/ {sessionData.scheduledDuration}m</span>
          </div>

          {/* Participants toggle */}
          <button
            onClick={() => { setIsParticipantsOpen((p) => !p); setIsChatOpen(false); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isParticipantsOpen ? 'bg-primary/20 text-primary' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            <Icon name="UsersIcon" size={14} />
            <span>{participants.length}</span>
          </button>

          {/* Chat toggle */}
          <button
            onClick={() => { setIsChatOpen((p) => !p); setIsParticipantsOpen(false); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isChatOpen ? 'bg-primary/20 text-primary' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            <Icon name="ChatBubbleLeftRightIcon" size={14} />
            <span>Chat</span>
          </button>

          {/* Fullscreen */}
          <button
            onClick={() => setIsFullscreen((f) => !f)}
            className="p-1.5 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 transition-colors"
          >
            <Icon name={isFullscreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} size={16} />
          </button>
        </div>
      </div>

      {/* ── Main Area ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Video Grid ── */}
        <div className="flex-1 flex flex-col p-4 gap-3 overflow-hidden">
          {/* Main (pinned) video */}
          <div className="flex-1 relative rounded-2xl overflow-hidden bg-[#161a24] border border-white/5 min-h-0">
            {/* Camera off overlay */}
            {pinnedParticipant_?.isCameraOff ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-3xl font-bold text-white">
                  {pinnedParticipant_?.initials}
                </div>
                <p className="text-white/50 text-sm">Camera is off</p>
              </div>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a2035] to-[#0d1520] flex items-center justify-center">
                {/* Simulated video feed */}
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-4xl font-bold text-white/80">
                  {pinnedParticipant_?.initials}
                </div>
                {/* Screen share indicator */}
                {pinnedParticipant_?.isScreenSharing && (
                  <div className="absolute inset-0 bg-[#0d1520] flex flex-col items-center justify-center gap-3">
                    <Icon name="ComputerDesktopIcon" size={48} className="text-primary/60" />
                    <p className="text-white/50 text-sm">Screen sharing active</p>
                  </div>
                )}
              </div>
            )}

            {/* Speaking indicator */}
            {pinnedParticipant_?.isSpeaking && !pinnedParticipant_?.isMuted && (
              <div className="absolute inset-0 rounded-2xl border-2 border-emerald-400/60 pointer-events-none" />
            )}

            {/* Name tag */}
            <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-1.5">
              <span className="text-white text-sm font-medium">{pinnedParticipant_?.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${pinnedParticipant_?.role === 'consultant' ? 'bg-primary/30 text-primary' : 'bg-white/10 text-white/60'}`}>
                {pinnedParticipant_?.role}
              </span>
              {pinnedParticipant_?.isMuted && (
                <Icon name="MicrophoneIcon" size={12} className="text-red-400" />
              )}
            </div>

            {/* Pin button */}
            <button
              onClick={() => setPinnedParticipant(null)}
              className="absolute top-3 right-3 p-1.5 bg-black/40 backdrop-blur-sm rounded-lg text-white/50 hover:text-white transition-colors"
            >
              <Icon name="ViewfinderCircleIcon" size={16} />
            </button>
          </div>

          {/* Thumbnail strip */}
          {thumbnailParticipants.length > 0 && (
            <div className="flex gap-3 h-32 flex-shrink-0">
              {thumbnailParticipants.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPinnedParticipant(p.id)}
                  className="relative flex-1 rounded-xl overflow-hidden bg-[#161a24] border border-white/5 hover:border-primary/40 transition-colors group"
                >
                  {p.isCameraOff ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">
                        {p.initials}
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#1a2035] to-[#0d1520] flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-sm font-bold text-white/80">
                        {p.initials}
                      </div>
                    </div>
                  )}
                  {p.isSpeaking && !p.isMuted && (
                    <div className="absolute inset-0 rounded-xl border-2 border-emerald-400/60 pointer-events-none" />
                  )}
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                    <span className="text-white text-xs font-medium truncate bg-black/50 rounded px-1.5 py-0.5">{p.name.split(' ')[0]}</span>
                    {p.isMuted && <Icon name="MicrophoneIcon" size={10} className="text-red-400" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Side Panel (Chat / Participants) ── */}
        {(isChatOpen || isParticipantsOpen) && (
          <div className="w-72 flex flex-col bg-[#111318] border-l border-white/5 flex-shrink-0">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <h3 className="text-white text-sm font-semibold">
                {isChatOpen ? 'Session Chat' : 'Participants'}
              </h3>
              <button
                onClick={() => { setIsChatOpen(false); setIsParticipantsOpen(false); }}
                className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
              >
                <Icon name="XMarkIcon" size={16} />
              </button>
            </div>

            {/* Participants panel */}
            {isParticipantsOpen && (
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {participants.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center text-sm font-bold text-white">
                        {p.initials}
                      </div>
                      {p.isSpeaking && !p.isMuted && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#111318]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{p.name}</p>
                      <p className="text-white/40 text-xs capitalize">{p.role}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {p.isMuted ? (
                        <Icon name="MicrophoneIcon" size={14} className="text-red-400" />
                      ) : (
                        <Icon name="MicrophoneIcon" size={14} className="text-emerald-400" />
                      )}
                      {p.isCameraOff ? (
                        <Icon name="VideoCameraSlashIcon" size={14} className="text-red-400" />
                      ) : (
                        <Icon name="VideoCameraIcon" size={14} className="text-emerald-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Chat panel */}
            {isChatOpen && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg) => {
                    const isMe = msg.sender === sessionData.client.name;
                    return (
                      <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                        {!isMe && (
                          <span className="text-white/40 text-xs mb-1 px-1">{msg.sender}</span>
                        )}
                        <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${isMe ? 'bg-primary/20 text-white rounded-br-sm' : 'bg-white/8 text-white/90 rounded-bl-sm'}`}>
                          {msg.text}
                        </div>
                        <span className="text-white/20 text-xs mt-1 px-1">{msg.timestamp}</span>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 border-t border-white/5">
                  <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Type a message…"
                      className="flex-1 bg-transparent text-white text-sm placeholder-white/30 outline-none"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim()}
                      className="p-1.5 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 disabled:opacity-30 transition-colors"
                    >
                      <Icon name="PaperAirplaneIcon" size={14} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Controls Bar ── */}
      <div className="flex items-center justify-center gap-3 px-6 py-4 bg-[#0d0f14]/95 backdrop-blur border-t border-white/5">
        {/* Mute */}
        <button
          onClick={() => setIsMuted((m) => !m)}
          className={`flex flex-col items-center gap-1.5 group`}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isMuted ? 'bg-red-500/20 border border-red-500/40 text-red-400' : 'bg-white/10 border border-white/10 text-white hover:bg-white/15'}`}>
            <Icon name={isMuted ? 'MicrophoneIcon' : 'MicrophoneIcon'} size={20} />
          </div>
          <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">
            {isMuted ? 'Unmute' : 'Mute'}
          </span>
        </button>

        {/* Camera */}
        <button
          onClick={() => setIsCameraOff((c) => !c)}
          className="flex flex-col items-center gap-1.5 group"
          title={isCameraOff ? 'Start Camera' : 'Stop Camera'}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isCameraOff ? 'bg-red-500/20 border border-red-500/40 text-red-400' : 'bg-white/10 border border-white/10 text-white hover:bg-white/15'}`}>
            <Icon name={isCameraOff ? 'VideoCameraSlashIcon' : 'VideoCameraIcon'} size={20} />
          </div>
          <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">
            {isCameraOff ? 'Start Cam' : 'Stop Cam'}
          </span>
        </button>

        {/* Screen Share */}
        <button
          onClick={toggleScreenShare}
          className="flex flex-col items-center gap-1.5 group"
          title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isScreenSharing ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-white/10 border border-white/10 text-white hover:bg-white/15'}`}>
            <Icon name="ComputerDesktopIcon" size={20} />
          </div>
          <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">
            {isScreenSharing ? 'Stop Share' : 'Share'}
          </span>
        </button>

        {/* Chat */}
        <button
          onClick={() => { setIsChatOpen((p) => !p); setIsParticipantsOpen(false); }}
          className="flex flex-col items-center gap-1.5 group"
          title="Chat"
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isChatOpen ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-white/10 border border-white/10 text-white hover:bg-white/15'}`}>
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
          </div>
          <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">Chat</span>
        </button>

        {/* Participants */}
        <button
          onClick={() => { setIsParticipantsOpen((p) => !p); setIsChatOpen(false); }}
          className="flex flex-col items-center gap-1.5 group"
          title="Participants"
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${isParticipantsOpen ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-white/10 border border-white/10 text-white hover:bg-white/15'}`}>
            <Icon name="UsersIcon" size={20} />
          </div>
          <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">People</span>
        </button>

        {/* Divider */}
        <div className="w-px h-10 bg-white/10 mx-1" />

        {/* End Session */}
        <button
          onClick={() => setShowEndModal(true)}
          className="flex flex-col items-center gap-1.5 group"
          title="End Session"
        >
          <div className="w-14 h-12 rounded-2xl flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-all duration-200 shadow-lg shadow-red-500/20">
            <Icon name="PhoneXMarkIcon" size={22} />
          </div>
          <span className="text-red-400/70 text-xs group-hover:text-red-400 transition-colors">End</span>
        </button>
      </div>

      {/* ── End Session Modal ── */}
      {showEndModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#161a24] border border-white/10 rounded-2xl p-7 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <Icon name="PhoneXMarkIcon" size={24} className="text-red-400" />
            </div>
            <h3 className="text-white text-lg font-bold text-center mb-2">End Session?</h3>
            <p className="text-white/50 text-sm text-center mb-6">
              This will end the session for all participants. Duration: <span className="text-white font-medium">{formatTime(elapsedSeconds)}</span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/15 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEndSession}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
